import { useMemo, useState } from 'react'
import {Dumbbell, Repeat} from 'lucide-react'
import { SPLIT_LABELS, generateWorkout, type SplitType } from '@/lib/plans'

const SPLITS: SplitType[] = ['ppl', 'upperlower', 'fullbody']

function WorkoutBuilder() {
  const [split, setSplit] = useState<SplitType>('ppl')
  const days = useMemo(() => generateWorkout(split), [split])

  return (
    <section id="workout" className="relative py-20 lg:py-28 scroll-mt-20 bg-white/[0.012] border-y border-white/[0.05]">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <header className="max-w-2xl">
          <span className="font-mono text-[13px] uppercase tracking-[0.2em] text-blue-500">
            Training
          </span>
          <h2 className="font-display mt-3 text-[34px] sm:text-[42px] font-bold tracking-[-0.02em] text-white">
            The best way to train to get ripped
          </h2>
          <p className="mt-4 text-[16px] leading-relaxed text-slate-400">
            Build your routine around metabolic resistance training — the proven way to shred muscle
            fat while staying strong. Pick a split below, from a classic bicep shredder pull day to
            full-body circuits engineered for a shredded lean body.
          </p>
        </header>

        {/* Split selector */}
        <div className="mt-9 inline-flex flex-wrap gap-2 p-1.5 rounded-2xl bg-white/[0.03] border border-white/[0.06]">
          {SPLITS.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setSplit(s)}
              className={`inline-flex items-center gap-2 rounded-xl px-5 py-2.5 text-[14px] font-semibold transition-colors ${
                split === s ? 'bg-blue-600 text-white' : 'text-slate-400 hover:text-white'
              }`}
            >
              <Dumbbell className="w-4 h-4" aria-hidden="true" />
              {SPLIT_LABELS[s]}
            </button>
          ))}
        </div>

        {/* Workout days */}
        <div className="mt-9 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {days.map((d) => (
            <article
              key={d.day}
              className="rounded-3xl border border-white/[0.07] bg-white/[0.02] p-6"
            >
              <div className="flex items-center justify-between">
                <span className="font-mono text-[12px] uppercase tracking-wide text-blue-400">
                  {d.day}
                </span>
                <Repeat className="w-4 h-4 text-slate-600" aria-hidden="true" />
              </div>
              <h3 className="font-display text-[16px] font-semibold text-white mt-2 leading-snug">
                {d.focus}
              </h3>

              <ul className="mt-5 space-y-3">
                {d.exercises.map((ex) => (
                  <li
                    key={ex.name}
                    className="flex items-center justify-between gap-3 border-b border-white/[0.05] pb-3 last:border-0 last:pb-0"
                  >
                    <span className="text-[14px] text-slate-300">{ex.name}</span>
                    <span className="font-mono text-[12px] text-slate-500 whitespace-nowrap">
                      {ex.scheme}
                    </span>
                  </li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      </div>
    </section>
  )
}

export default WorkoutBuilder
